import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient, { uploadFormData, API_BASE_URL } from '../utils/apiClient';
import './Add.css'; // We can reuse the Add page styles
import '../utils/loadingStyles.css'; // Import the dedicated loading styles
import TagSelector from '../components/TagSelector';
import authClient from '../utils/authClient';
import ProgressModal from '../components/ProgressModal';

// List of available emulators in the system
const AVAILABLE_EMULATORS = [
  { value: 'ejs', label: 'EmulatorJS' },
  { value: 'ruffle', label: 'Ruffle (Flash)' },
  { value: 'apple2ts', label: 'Apple II TS' },
  { value: 'html', label: 'HTML (Web Games)' },
  { value: 'jsdos', label: 'JS-DOS (DOS Games)' },
  { value: 'emularity', label: 'Emularity (MAME)' },
  { value: 'encrusted', label: 'Encrusted' },
  { value: 'freej2me', label: 'FreeJ2ME (Java ME)' }
];

const Update = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      if (!authClient.isAuthenticated()) {
        // Redirect to login page with current location for redirect after login
        navigate('/login', { state: { from: { pathname: `/update/${id}` } } });
      } else {
        setIsAuthenticated(true);
      }
    };
    
    checkAuth();
  }, [navigate, id]);
  const [game, setGame] = useState({
    titleeng: '',
    titlechn: '',
    desc: '',
    additional_hint: '',
    platform: '',
    cover: '',
    manual: '',
    year: '',
    dev: '',
    publisher: '',
    translator: '',
    loadasname: '',
    launchoptions: '',
    multiplayer: 0,
    emulator: null, // Custom emulator override (null = use platform default)
    mobileoptimized: 1 // Default to 1 (yes/optimized)
  });

  // Add state for platforms
  const [platforms, setPlatforms] = useState([]);
  const [platformsLoading, setPlatformsLoading] = useState(true);

  // Add state for multiple manuals
  const [manuals, setManuals] = useState([]);
  const [newManualName, setNewManualName] = useState('');

  const [files, setFiles] = useState({
    cover: null
  });

  // ROM Management State
  const [romMode, setRomMode] = useState('single'); // 'single' or 'multiple'
  const [roms, setRoms] = useState([]);
  const [romToDelete, setRomToDelete] = useState([]);

  // ROM types available
  const romTypeOptions = [
    { value: 'default', label: '默认' },
    { value: 'europe', label: '欧版' },
    { value: 'usa', label: '美版' },
    { value: 'jpn', label: '日版' },
    { value: 'chn', label: '简中版' },
    { value: 'tchn', label: '繁中版' },
    { value: 'custom', label: '自定义' }
  ];

  // Add state for manual files with index
  const [manualFiles, setManualFiles] = useState({});

  const [fileNames, setFileNames] = useState({
    cover: ''
  });

  // Add state for manual filenames with index
  const [manualFileNames, setManualFileNames] = useState({});

  const [fileSizes, setFileSizes] = useState({
    cover: ''
  });

  // Add state for manual file sizes with index
  const [manualFileSizes, setManualFileSizes] = useState({});

  const [compressCover, setCompressCover] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  // Add loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);

  // Progress modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');
  const [modalError, setModalError] = useState(null);

  // Define update steps - reflecting actual process
  const updateSteps = [
    {
      title: '验证数据',
      description: '检查表单数据和文件完整性'
    },
    {
      title: '准备更新',
      description: '打包所有更改的文件和数据'
    },
    {
      title: '上传到服务器',
      description: '批量上传更改的文件到服务器'
    },
    {
      title: '完成',
      description: '游戏更新成功，正在跳转...'
    }
  ];

  // Add selectedTagIds to state variables at the start of the component
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Add state for custom emulator checkbox
  const [useCustomEmulator, setUseCustomEmulator] = useState(false);

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper to convert ROM paths to specific ROM types
  const getRomTypeFromPath = (path) => {
    if (!path) return 'default';
    
    if (path.includes('_europe.')) return 'europe';
    if (path.includes('_usa.')) return 'usa';
    if (path.includes('_jpn.')) return 'jpn';
    if (path.includes('_chn.')) return 'chn';
    if (path.includes('_tchn.')) return 'tchn';
    if (path.includes('_other')) return 'custom';
    
    return 'default';
  };

  // Helper to get custom type from path
  const getCustomTypeFromPath = (path) => {
    if (!path) return '';
    const match = path.match(/_other(\d+)\./);
    return match ? `自定义${parseInt(match[1]) + 1}` : '';
  };

  useEffect(() => {
    const fetchGame = async () => {
      setIsLoading(true);
      setLoadError(null);
      
      try {
        const res = await apiClient.get(`/games/${id}`);
        const gameData = res.data;
        
        // Remove rom, jpnrom, chnrom fields from the state object
        const { jpnrom, chnrom, ...restGameData } = gameData;
        setGame(restGameData);
        setCompressCover(gameData.compressed ? true : false);
        
        // Set custom emulator checkbox state based on existing emulator data
        if (gameData.emulator && gameData.emulator !== null) {
          setUseCustomEmulator(true);
        } else {
          setUseCustomEmulator(false);
        }
        
        // Process ROM data
        if (gameData.rom) {
          let romsData = [];
          
          try {
            // Try to parse as JSON
            romsData = JSON.parse(gameData.rom);
            
            // Convert to our ROM format
            const processedRoms = romsData.map(romData => {
              const type = getRomTypeFromPath(romData.path);
              const customType = type === 'custom' ? getCustomTypeFromPath(romData.path) : '';
              
              return {
                type,
                customType,
                existingPath: romData.path,
                fileName: romData.path ? romData.path.split('/').pop() : '',
                fileSize: gameData.romSizes ? gameData.romSizes[romsData.indexOf(romData)] : 'N/A',
                file: null
              };
            });
            
            setRoms(processedRoms);
            setRomMode(processedRoms.length > 1 ? 'multiple' : 'single');
          } catch (e) {
            // Handle legacy format - create a single ROM entry
            console.error('Error parsing ROM JSON, using legacy format:', e);
            
            const legacyRoms = [];
            
            // Original ROM
            if (gameData.rom) {
              legacyRoms.push({
                type: 'default',
                customType: '',
                existingPath: gameData.rom,
                fileName: gameData.rom ? gameData.rom.split('/').pop() : '',
                fileSize: gameData.romSize || 'N/A',
                file: null
              });
            }
            
            // Japanese ROM if present
            if (gameData.jpnrom) {
              legacyRoms.push({
                type: 'jpn',
                customType: '',
                existingPath: gameData.jpnrom,
                fileName: gameData.jpnrom ? gameData.jpnrom.split('/').pop() : '',
                fileSize: gameData.jpnromSize || 'N/A',
                file: null
              });
            }
            
            // Chinese ROM if present
            if (gameData.chnrom) {
              legacyRoms.push({
                type: 'chn',
                customType: '',
                existingPath: gameData.chnrom,
                fileName: gameData.chnrom ? gameData.chnrom.split('/').pop() : '',
                fileSize: gameData.chnromSize || 'N/A',
                file: null
              });
            }
            
            setRoms(legacyRoms);
            setRomMode(legacyRoms.length > 1 ? 'multiple' : 'single');
          }
        } else if (gameData.jpnrom || gameData.chnrom) {
          // Edge case: no rom but has jpnrom or chnrom
          const legacyRoms = [];
          
          // Japanese ROM if present
          if (gameData.jpnrom) {
            legacyRoms.push({
              type: 'jpn',
              customType: '',
              existingPath: gameData.jpnrom,
              fileName: gameData.jpnrom ? gameData.jpnrom.split('/').pop() : '',
              fileSize: gameData.jpnromSize || 'N/A',
              file: null
            });
          }
          
          // Chinese ROM if present
          if (gameData.chnrom) {
            legacyRoms.push({
              type: 'chn',
              customType: '',
              existingPath: gameData.chnrom,
              fileName: gameData.chnrom ? gameData.chnrom.split('/').pop() : '',
              fileSize: gameData.chnromSize || 'N/A',
              file: null
            });
          }
          
          setRoms(legacyRoms);
          setRomMode(legacyRoms.length > 1 ? 'multiple' : 'single');
        } else {
          // No ROMs
          setRoms([{ type: 'default', customType: '', file: null, fileName: '', fileSize: '' }]);
          setRomMode('single');
        }
        
        // Parse manual JSON if it exists
        if (gameData.manual) {
          try {
            const parsedManuals = JSON.parse(gameData.manual);
            if (Array.isArray(parsedManuals)) {
              setManuals(parsedManuals);
            } else {
              // Handle case where manual is not an array
              console.warn('Manual data is not in expected array format');
              setManuals([]); // Set empty array instead of trying to handle legacy format
            }
          } catch (e) {
            console.error('Error parsing manual JSON:', e);
            setManuals([]); // Set empty array instead of trying to handle legacy format
          }
        }
      } catch (err) {
        console.error('Error fetching game:', err);
        setLoadError(`加载游戏失败: ${err.message || '未知错误'}`);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGame();
  }, [id]);

  // Load platforms
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        setPlatformsLoading(true);
        const response = await apiClient.get('/platforms');
        setPlatforms(response.data);
      } catch (err) {
        console.error('Error fetching platforms:', err);
      } finally {
        setPlatformsLoading(false);
      }
    };

    fetchPlatforms();
  }, []);

  const handleChange = (e) => {
    setGame(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Add a handler for checkbox changes
  const handleCheckboxChange = (e) => {
    setGame(prev => ({ ...prev, [e.target.name]: e.target.checked ? 1 : 0 }));
  };

  // Handler for custom emulator checkbox
  const handleCustomEmulatorChange = (e) => {
    const checked = e.target.checked;
    setUseCustomEmulator(checked);
    if (!checked) {
      // Reset emulator to null when unchecked
      setGame(prev => ({ ...prev, emulator: null }));
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [type]: file }));
      setFileNames(prev => ({ ...prev, [type]: file.name }));
      setFileSizes(prev => ({ ...prev, [type]: formatFileSize(file.size) }));
      // Create a temporary URL for preview
      const url = URL.createObjectURL(file);
      setGame(prev => ({ ...prev, [type]: url }));
    }
  };

  // Handle ROM file changes
  const handleRomFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setRoms(prev => prev.map((rom, i) => 
        i === index 
          ? { ...rom, file, fileName: file.name, fileSize: formatFileSize(file.size) } 
          : rom
      ));
    }
  };

  // Handle ROM type selection
  const handleRomTypeChange = (e, index) => {
    const type = e.target.value;
    setRoms(prev => prev.map((rom, i) => 
      i === index ? { ...rom, type } : rom
    ));
  };

  // Handle custom ROM type input
  const handleCustomTypeChange = (e, index) => {
    const customType = e.target.value;
    setRoms(prev => prev.map((rom, i) => 
      i === index ? { ...rom, customType } : rom
    ));
  };

  // Add a new ROM entry
  const addRom = () => {
    setRoms(prev => [...prev, { type: 'default', customType: '', file: null, fileName: '', fileSize: '' }]);
  };

  // Remove a ROM entry
  const removeRom = (index) => {
    const romToRemove = roms[index];
    
    // If this ROM has an existing path, add it to the delete list
    if (romToRemove.existingPath) {
      setRomToDelete(prev => [...prev, romToRemove.existingPath]);
    }
    
    // Remove the ROM from the list
    if (roms.length > 1) {
      setRoms(roms.filter((_, i) => i !== index));
    } else {
      // If this is the last ROM, just clear it instead of removing
      setRoms([{ type: 'default', customType: '', file: null, fileName: '', fileSize: '' }]);
    }
  };

  // Switch between single and multiple ROM modes
  const handleRomModeChange = (mode) => {
    if (mode === 'single' && romMode === 'multiple') {
      // If switching from multiple to single, keep only the first ROM and mark others for deletion
      if (roms.length > 1) {
        const additionalRoms = roms.slice(1);
        additionalRoms.forEach(rom => {
          if (rom.existingPath) {
            setRomToDelete(prev => [...prev, rom.existingPath]);
          }
        });
      }
      setRoms([roms[0] || { type: 'default', customType: '', file: null, fileName: '', fileSize: '' }]);
    }
    
    setRomMode(mode);
  };

  // Handle manual file changes with index
  const handleManualFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setManualFiles(prev => ({ ...prev, [index]: file }));
      setManualFileNames(prev => ({ ...prev, [index]: file.name }));
      setManualFileSizes(prev => ({ ...prev, [index]: formatFileSize(file.size) }));
      
      // Update the manual's temporary preview URL
      setManuals(prev => prev.map((manual, i) => 
        i === index 
          ? { ...manual, tempUrl: URL.createObjectURL(file) } 
          : manual
      ));
    }
  };

  // Function to add a new manual
  const addManual = () => {
    if (newManualName.trim() === '') {
      alert('请输入手册名称');
      return;
    }
    
    setManuals(prev => [...prev, { name: newManualName, path: null }]);
    setNewManualName('');
  };

  // Function to remove a manual
  const removeManual = (index) => {
    // Check if this manual has a file being uploaded
    if (manualFiles[index]) {
      URL.revokeObjectURL(manuals[index].tempUrl);
      
      // Remove the file from the state
      const updatedFiles = { ...manualFiles };
      delete updatedFiles[index];
      setManualFiles(updatedFiles);
      
      const updatedFileNames = { ...manualFileNames };
      delete updatedFileNames[index];
      setManualFileNames(updatedFileNames);
      
      const updatedFileSizes = { ...manualFileSizes };
      delete updatedFileSizes[index];
      setManualFileSizes(updatedFileSizes);
    }
    
    // Remove the manual from the array
    setManuals(prev => prev.filter((_, i) => i !== index));
  };

  // Function to update manual name
  const updateManualName = (index, newName) => {
    if (newName.trim() === '') {
      alert('手册名称不能为空');
      return;
    }
    
    setManuals(prev => prev.map((manual, i) => 
      i === index ? { ...manual, name: newName } : manual
    ));
  };

  const handleCompress = async () => {
    // If already compressed, ask for confirmation
    if (game.compressed) {
      if (!window.confirm("此图片已经压缩过。再次压缩可能会导致质量损失。继续吗?")) {
        return;
      }
    }

    setIsCompressing(true);
    try {
      const response = await apiClient.post("/compress-image", {
        id,
        type: 'games',
        filePath: game.cover
      });

      // Update the game state with the new compression status
      setGame(prev => ({
        ...prev,
        compressed: 1,
        coverSize: response.data.formattedSize
      }));
      setCompressCover(true);

      alert("图片压缩成功!");
    } catch (error) {
      console.error("Error compressing image:", error);
      alert(`压缩图片时出错: ${error.message}。请重试。`);
    } finally {
      setIsCompressing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setModalError(null);
    setShowProgressModal(true);
    setCurrentStep(1);
    setUploadProgress(0);
    setCurrentFileName('');
    
    // Check if at least one ROM is present (file or existing path)
    const hasRom = roms.some(rom => rom.file || rom.existingPath);
    if (!hasRom) {
      setModalError("至少需要一个ROM文件。请上传ROM文件或保留现有ROM。");
      setShowProgressModal(true);
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Step 1: Validate data
      setCurrentStep(1);
      setCurrentFileName('检查更新数据...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Step 2: Prepare update
      setCurrentStep(2);
      setCurrentFileName('准备更新数据包...');
      
      // Create FormData object
      const formData = new FormData();
      
      // Add text fields - include all fields, even if empty
      Object.keys(game).forEach(key => {
        // Exclude fields we're handling separately
        if (key !== 'cover' && key !== 'manual' && key !== 'rom' && key !== 'jpnrom' && key !== 'chnrom' && key !== 'emulator') {
          formData.append(key, game[key] === null ? '' : game[key]);
        }
      });
      
      // Add emulator field only if custom emulator is selected
      if (useCustomEmulator && game.emulator) {
        formData.append('emulator', game.emulator);
      } else {
        // Explicitly send null to clear any existing custom emulator
        formData.append('emulator', '');
      }

      // Add compression option
      formData.append('compressed', compressCover ? '1' : '0');

      // Convert manuals array to JSON and add to form data
      const manualsToUpload = [...manuals].map(({ tempUrl, fileSize, ...manual }) => manual);
      formData.append('manual', JSON.stringify(manualsToUpload));

      // Add cover file if changed
      if (files.cover) {
        setCurrentFileName(`更新封面: ${files.cover.name}`);
        // Check file size
        if (files.cover.size > 500 * 1024 * 1024) {
          throw new Error(`Cover image is too large. Maximum size is 500MB.`);
        }
        formData.append('cover', files.cover);
      }

      // Add ROM files and types based on mode
      if (romMode === 'single') {
        // For single ROM mode, just add the file without type information
        if (roms[0].file) {
          if (roms[0].file.size > 500 * 1024 * 1024) {
          throw new Error(`ROM file is too large. Maximum size is 500MB.`);
        }
          formData.append('rom', roms[0].file);
          formData.append('romTypes', JSON.stringify(['default']));
      }
      } else {
        // For multiple ROM mode
        const romTypes = [];
        const customRomTypes = {};
        
        // Add each ROM file that has been changed
        roms.forEach((rom, index) => {
          if (rom.file) {
            setCurrentFileName(`更新ROM ${index + 1}: ${rom.file.name}`);
            if (rom.file.size > 500 * 1024 * 1024) {
              throw new Error(`ROM file ${index + 1} is too large. Maximum size is 500MB.`);
        }
            formData.append('rom', rom.file);
            romTypes.push(rom.type);
            
            // Store custom type if applicable
            if (rom.type === 'custom') {
              customRomTypes[index] = rom.customType || `自定义${index + 1}`;
            }
          } else if (rom.existingPath) {
            // Include type info for existing ROMs too
            romTypes.push(rom.type);
            if (rom.type === 'custom') {
              customRomTypes[index] = rom.customType || `自定义${index + 1}`;
            }
          }
        });
        
        // Add ROM types and custom types information
        formData.append('romTypes', JSON.stringify(romTypes));
        formData.append('customRomTypes', JSON.stringify(customRomTypes));
      }
      
      // Add list of ROMs to delete
      if (romToDelete.length > 0) {
        formData.append('deleteRoms', JSON.stringify(romToDelete));
      }

      // Add manual files with indexed field names
      Object.keys(manualFiles).forEach(index => {
        if (manualFiles[index]) {
          setCurrentFileName(`更新手册: ${manualFiles[index].name}`);
          // Check file size
          if (manualFiles[index].size > 500 * 1024 * 1024) {
            throw new Error(`Manual file is too large. Maximum size is 500MB.`);
          }
          formData.append(`manual_${index}`, manualFiles[index]);
        }
      });

      // Add selected tag IDs to the form data for backend processing
      if (selectedTagIds.length > 0) {
        formData.append('tagIds', JSON.stringify(selectedTagIds));
      }

      console.log('Submitting form data with ROMs:', {
        romMode,
        roms: roms.map(rom => ({ 
          type: rom.type, 
          fileName: rom.fileName, 
          existingPath: rom.existingPath,
          hasFile: !!rom.file 
        })),
        romToDelete,
        manuals: manualsToUpload
      });

      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Step 3: Upload to server (this is where the real upload happens)
      setCurrentStep(3);
      setCurrentFileName('正在上传更改的文件到服务器...');
      
      // Use our centralized uploadFormData function with PUT method and progress tracking
      const data = await uploadFormData(`/games/${id}`, formData, 'PUT', (progress) => {
        setUploadProgress(progress);
        if (progress < 100) {
          setCurrentFileName(`上传中... ${progress}%`);
        }
      });
      console.log('Server response:', data);
      
      // Step 4: Complete
      setCurrentStep(4);
      setCurrentFileName('更新完成');
      setUploadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Hide progress modal and navigate
      setShowProgressModal(false);
      
      // Use timeout to ensure server has processed the update 
      // before navigating back to manage page
      setTimeout(() => {
        navigate("/manage");
      }, 500);
    } catch (err) {
      console.error('Error submitting form:', err);
      
      // Show error in the progress modal and keep it open
      setCurrentFileName(`错误: ${err.message}`);
      setUploadProgress(0);
      setSubmitError(`更新游戏失败: ${err.message || '未知错误'}`);
      setShowProgressModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Replace the platform text input with a select dropdown
  const renderPlatformSelect = () => {
    if (platformsLoading) {
      return <div className="loading-field">Loading platforms...</div>;
    }
    
    return (
      <select
        name="platform"
        value={game.platform}
        onChange={handleChange}
        required
        className="emulator-select"
      >
        <option value="">-- 选择平台 --</option>
        {platforms.map(platform => (
          <option key={platform.id} value={platform.id}>
            {platform.nameeng} {platform.namechn ? `(${platform.namechn})` : ''}
          </option>
        ))}
      </select>
    );
  };

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="admin-loading-container">
        <h2>正在加载游戏数据...</h2>
        <div className="admin-loading-spinner"></div>
      </div>
    );
  }

  // Show error state if failed to load data
  if (loadError) {
    return (
      <div className="admin-error-container">
        <h2>加载游戏数据失败</h2>
        <p>{loadError}</p>
        <button 
          className="admin-retry-button"
          onClick={() => navigate("/manage")}
        >
          返回管理页面
        </button>
      </div>
    );
  }

  // Only render the form if user is authenticated
  if (!isAuthenticated) {
    return <div className="loading-container"><div className="loading"></div></div>;
  }
  
  return (
    <>
      <ProgressModal
        isVisible={showProgressModal}
        currentStep={currentStep}
        progress={uploadProgress}
        fileName={currentFileName}
        totalSteps={updateSteps.length}
        steps={updateSteps}
        isError={!!modalError}
        errorMessage={modalError}
        onClose={() => {
          setShowProgressModal(false);
          setModalError(null);
        }}
      />
      <div className="add-container">
        <h1>更新游戏</h1>
        {submitError && (
          <div className="error-message">
            <p>{submitError}</p>
          </div>
        )}
      
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-group">
          <label>平台: *</label>
          {renderPlatformSelect()}
        </div>

        {/* Custom Emulator Section */}
        <div className="form-group checkbox-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={useCustomEmulator}
              onChange={handleCustomEmulatorChange}
              className="custom-checkbox"
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">使用非标准模拟器</span>
          </label>
          <div className="field-hint">
            勾选此项以覆盖平台默认模拟器设置
          </div>
        </div>

        {useCustomEmulator && (
          <div className="form-group">
            <label>自定义模拟器: *</label>
            <select
              name="emulator"
              value={game.emulator || ''}
              onChange={handleChange}
              required
              className="emulator-select"
            >
              <option value="">-- 选择模拟器 --</option>
              {AVAILABLE_EMULATORS.map(emulator => (
                <option key={emulator.value} value={emulator.value}>
                  {emulator.label}
                </option>
              ))}
            </select>
            <div className="field-hint">
              选择此游戏专用的模拟器，将覆盖平台默认设置
            </div>
          </div>
        )}

        <div className="form-group">
          <label>英文标题:</label>
          <input
            type="text"
            name="titleeng"
            value={game.titleeng}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>中文标题:</label>
          <input
            type="text"
            name="titlechn"
            value={game.titlechn}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>描述:</label>
          <textarea
            name="desc"
            value={game.desc}
            onChange={handleChange}
            rows="4"
          />
        </div>

        <div className="form-group">
          <label>额外提示 (显示在控制说明上方):</label>
          <textarea
            name="additional_hint"
            value={game.additional_hint || ''}
            onChange={handleChange}
            rows="3"
            placeholder="可选：添加游戏特定的操作提示或注意事项"
          />
        </div>

        <div className="form-group">
          <label>发行年份:</label>
          <input
            type="number"
            name="year"
            value={game.year || ''}
            onChange={handleChange}
            placeholder="例如: 1998"
          />
        </div>

        <div className="form-group">
          <label>开发商:</label>
          <input
            type="text"
            name="dev"
            value={game.dev}
            onChange={handleChange}
            placeholder="例如: 任天堂"
          />
        </div>

        <div className="form-group">
          <label>发行商:</label>
          <input
            type="text"
            name="publisher"
            value={game.publisher}
            onChange={handleChange}
            placeholder="例如: 任天堂"
          />
        </div>

        <div className="form-group">
          <label>汉化组:</label>
          <input
            type="text"
            name="translator"
            value={game.translator}
            onChange={handleChange}
            placeholder="e.g. 无名汉化组"
          />
          <div className="field-hint">
            对于中文ROM，请填写汉化组或翻译者名称
          </div>
        </div>

        <div className="form-group">
          <label>加载名称 (MAME游戏专用):</label>
          <input
            type="text"
            name="loadasname"
            value={game.loadasname}
            onChange={handleChange}
            placeholder="e.g. sf2.zip"
          />
          <div className="field-hint">
            对于MAME游戏，请输入模拟器应使用的确切ROM文件名（包括扩展名）
          </div>
        </div>

        <div className="form-group">
          <label>启动选项:</label>
          <input
            type="text"
            name="launchoptions"
            value={game.launchoptions}
            onChange={handleChange}
            placeholder="游戏的可选参数"
          />
          <div className="field-hint">
            该游戏特定的可选参数（某些模拟器使用）
          </div>
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="multiplayer"
              checked={game.multiplayer === 1}
              onChange={handleCheckboxChange}
              className="custom-checkbox"
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">多人游戏支持</span>
          </label>
          <div className="field-hint">
            如果游戏支持多人模式，请启用此选项（适用于EJS和JS-DOS模拟器）
          </div>
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="mobileoptimized"
              checked={game.mobileoptimized === 1}
              onChange={handleCheckboxChange}
              className="custom-checkbox"
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">移动端优化</span>
          </label>
          <div className="field-hint">
            游戏是否针对移动设备优化（未优化的游戏将在移动端Feed中被过滤）
          </div>
        </div>

        <div className="form-group">
          <label>标签:</label>
          <TagSelector 
            gameId={id}  // Include gameId to fetch existing tags
            onChange={(selectedTags) => {
              // Store selected tag IDs in component state
              setSelectedTagIds(selectedTags.map(tag => tag.id));
            }}
          />
          <div className="field-hint">
            选择描述此游戏的标签（类型、特点等）
          </div>
        </div>

        <div className="form-group">
          <label>封面图片:</label>
          <div className="input-group">
            <input
              type="text"
              name="cover"
              value={game.cover}
              onChange={handleChange}
              placeholder="封面图片URL或Blob"
              readOnly
            />
            <div className="file-input">
              <label className="file-label">
                {fileNames.cover || '上传文件'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'cover')}
                />
              </label>
            </div>
          </div>
          {fileSizes.cover && <div className="file-size-info">{fileSizes.cover}</div>}
          <div className="compress-option">
            <label className="checkbox-container">
              <input
                type="checkbox"
                id="compress-cover"
                checked={compressCover}
                onChange={(e) => setCompressCover(e.target.checked)}
                className="custom-checkbox"
              />
              <span className="checkmark"></span>
              <span className="checkbox-label">压缩新图片（使用TinyPNG）</span>
            </label>
          </div>
          {game.cover && (
            <div className="image-preview-container">
              <img src={typeof game.cover === 'string' && game.cover.startsWith('/') 
                ? `${API_BASE_URL}${game.cover}` 
                : game.cover} 
                alt="封面预览" className="preview" 
              />
              {game.coverSize && (
                <div className="file-info-container">
                  <div className="file-size-info">当前大小: {game.coverSize}</div>
                  <div className={game.compressed ? 'compressed-badge' : 'not-compressed-badge'}>
                    {game.compressed ? '已压缩' : '未压缩'}
                  </div>
                  {!files.cover && !isCompressing && (
                    <button 
                      type="button"
                      className="compress-button"
                      onClick={handleCompress}
                      disabled={isCompressing}
                    >
                      {game.compressed ? '重新压缩' : '压缩'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ROM Management Section */}
        <div className="form-group rom-section">
          <label>ROM文件: *</label>
          
          <div className="rom-mode-selector">
            <div className="rom-mode-option">
              <label className={`radio-container ${romMode === 'single' ? 'checked' : ''}`}>
                <input
                  type="radio"
                  id="rom-mode-single"
                  checked={romMode === 'single'}
                  onChange={() => handleRomModeChange('single')}
                  className="custom-radio"
                />
                <span className="radio-label">单个ROM</span>
              </label>
            </div>
            <div className="rom-mode-option">
              <label className={`radio-container ${romMode === 'multiple' ? 'checked' : ''}`}>
                <input
                  type="radio"
                  id="rom-mode-multiple"
                  checked={romMode === 'multiple'}
                  onChange={() => handleRomModeChange('multiple')}
                  className="custom-radio"
                />
                <span className="radio-label">多ROM版本</span>
              </label>
            </div>
          </div>
          
          <div className="roms-container">
            {roms.map((rom, index) => (
              <div key={index} className="rom-item">
                {romMode === 'multiple' && (
                  <div className="rom-header">
                    <div className="rom-type-selector">
                      <select
                        value={rom.type}
                        onChange={(e) => handleRomTypeChange(e, index)}
                        className="rom-type-select"
                      >
                        {romTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      
                      {rom.type === 'custom' && (
                        <input
                          type="text"
                          value={rom.customType}
                          onChange={(e) => handleCustomTypeChange(e, index)}
                          placeholder="输入自定义类型"
                          className="rom-custom-type"
                        />
                      )}
                    </div>
                    
                    {roms.length > 1 && (
                      <button
                        type="button"
                        className="remove-rom-button"
                        onClick={() => removeRom(index)}
                      >
                        移除
                      </button>
                    )}
                  </div>
                )}
                
                <div className="rom-file-section">
                  <div className="input-group">
                    <div className="file-input">
                      <label className="file-label">
                        {rom.fileName || '上传ROM'}
                        <input
                          type="file"
                          onChange={(e) => handleRomFileChange(e, index)}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="rom-file-info-row">
                    {rom.fileSize && (
                      <div className="file-size-info">{rom.fileSize}</div>
                    )}
                    {rom.existingPath && (
                      <div className="view-button-container">
                        <a 
                          href={`${API_BASE_URL}${rom.existingPath}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="view-button"
                        >
                          下载
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Add new ROM button (only in multiple mode) */}
            {romMode === 'multiple' && (
              <div className="add-rom-section">
                <button
                  type="button"
                  className="add-rom-button"
                  onClick={addRom}
                >
                  添加ROM版本
                </button>
              </div>
            )}
          </div>
          <div className="field-hint">
            {romMode === 'single' 
              ? "单个ROM将被保存为默认版本"
              : "多ROM模式允许上传不同区域或翻译版本的ROM文件"}
          </div>
        </div>

        {/* Multi-Manual Management Section */}
        <div className="form-group manuals-section">
          <label>Game Manuals:</label>
          <div className="manuals-container">
            {manuals.length === 0 ? (
              <div className="no-manuals-message">尚未添加手册</div>
            ) : (
              manuals.map((manual, index) => (
                <div key={index} className="manual-item">
                  <div className="manual-header">
                    <input
                      type="text"
                      value={manual.name}
                      onChange={(e) => updateManualName(index, e.target.value)}
                      placeholder="手册名称"
                      className="manual-name-input"
                    />
                  </div>
                  
                  <div className="manual-file-section">
                    <div className="input-group">
                      <div className="file-input">
                        <label className="file-label">
                          {manualFileNames[index] || '上传新文件'}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleManualFileChange(e, index)}
                          />
                        </label>
                      </div>
                    </div>
                    {manualFileSizes[index] && (
                      <div className="file-size-info">{manualFileSizes[index]}</div>
                    )}
                    <div className="manual-buttons-row">
                      <button
                        type="button"
                        className="remove-manual-button"
                        onClick={() => removeManual(index)}
                      >
                        删除
                      </button>
                      
                      {/* Show current manual link if it exists */}
                      {manual.path && (
                        <button 
                          onClick={() => window.open(`${API_BASE_URL}${manual.path}`, '_blank')} 
                          className="view-button"
                        >
                          查看
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
            
            {/* Add new manual section */}
            <div className="add-manual-section">
              <input
                type="text"
                value={newManualName}
                onChange={(e) => setNewManualName(e.target.value)}
                placeholder="输入手册名称..."
                className="manual-name-input"
              />
              <button
                type="button"
                className="add-manual-button"
                onClick={addManual}
              >
                添加手册
              </button>
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          className="submit-btn" 
          disabled={isSubmitting}
        >
          {isSubmitting ? '更新中...' : '更新游戏'}
        </button>
      </form>

      </div>
    </>
  );
};

export default Update;
